import { z } from "zod";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import {
    scheduleApplicationBaseSchema,
    scheduleApplicationPeriodBaseSchema,
} from "./schedule.schema";
import { scheduleRecognitionOverridesSchema } from "./scheduleTemplate.schema";

const applyScheduleCommonFields = {
    entityId: z.number().int().positive(),
    amount: z.number().positive(),
    specificDate: z.string().date().nullish(),
    ledgerId: z.string().uuid().optional(),
    /**
     * The user's answer to a linked-source conflict: remove these live
     * schedules on the matched invoice or payment (journal entries included)
     * and apply this one instead. Must cover every conflicting schedule.
     */
    replaceLinkedApplicationIds: z.array(z.string().uuid()).min(1).optional(),
    overrides: scheduleRecognitionOverridesSchema.optional(),
};

const transactionApplySchema = z.object({
    ...applyScheduleCommonFields,
    sourceType: z.literal(ScheduleSourceType.transaction),
    transactionId: z.number().int().positive(),
    termStart: z.string().date().optional(),
});

export const scheduleSourceLineIdsSchema = z
    .array(z.string().uuid())
    .min(1)
    .refine((ids) => new Set(ids).size === ids.length, {
        message: "Line ids must be unique",
    });

const invoiceApplySchema = z.object({
    ...applyScheduleCommonFields,
    sourceType: z.literal(ScheduleSourceType.invoice),
    invoiceId: z.string().uuid(),
    sourceLineIds: scheduleSourceLineIdsSchema.optional(),
    termStart: z.string().date().optional(),
});

const billApplySchema = z.object({
    ...applyScheduleCommonFields,
    sourceType: z.literal(ScheduleSourceType.bill),
    billId: z.string().uuid(),
    sourceLineIds: scheduleSourceLineIdsSchema.optional(),
    termStart: z.string().date().optional(),
});

const manualApplySchema = z.object({
    ...applyScheduleCommonFields,
    sourceType: z.literal(ScheduleSourceType.manual),
    manualSourceLabel: z.string().trim().min(1),
    termStart: z.string().date(),
    /**
     * A manual schedule on a deferring policy whose balance already sits on
     * the holding account from the opening balances: apply posts only the
     * releases, with no opening entry. `amount` is the balance remaining at
     * the entity's bookkeeping start, and the term can't start before it.
     */
    isMigratedSchedule: z.boolean().optional(),
});

export const applyScheduleTemplateBodySchema = z.discriminatedUnion(
    "sourceType",
    [
        transactionApplySchema,
        invoiceApplySchema,
        billApplySchema,
        manualApplySchema,
    ],
);

/**
 * A pending milestone row recognizes only once confirmed, so the apply
 * response marks it for the optimistic "left to recognize" figure.
 */
export const appliedSchedulePeriodSchema =
    scheduleApplicationPeriodBaseSchema.extend({
        milestone: z
            .object({ sortOrder: z.number().int().positive() })
            .nullable(),
    });

export type AppliedSchedulePeriod = z.infer<typeof appliedSchedulePeriodSchema>;

export const applyScheduleTemplateResponseSchema = z.object({
    application: scheduleApplicationBaseSchema,
    periods: z.array(appliedSchedulePeriodSchema),
});

export const APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT = 100;

export const applicationIdsSchema = z
    .array(z.string().uuid())
    .min(1)
    .max(APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT)
    .refine((ids) => new Set(ids).size === ids.length, {
        message: "Application ids must be unique",
    });

const bulkSourceCommonFields = {
    amount: z.number().positive(),
    /**
     * Schedules currently on this same source. They are deleted (reverse
     * mode) in the same transaction before the new one is applied — the bulk
     * "change schedule".
     */
    replaceApplicationIds: applicationIdsSchema.optional(),
};

const bulkTransactionSourceSchema = z.object({
    ...bulkSourceCommonFields,
    sourceType: z.literal(ScheduleSourceType.transaction),
    transactionId: z.number().int().positive(),
    /** Defaults as on a single apply. */
    termStart: z.string().date().optional(),
});

const bulkInvoiceSourceSchema = z.object({
    ...bulkSourceCommonFields,
    sourceType: z.literal(ScheduleSourceType.invoice),
    invoiceId: z.string().uuid(),
    /** Defaults as on a single apply. */
    termStart: z.string().date().optional(),
});

export const applyScheduleTemplateBulkSourceSchema = z.discriminatedUnion(
    "sourceType",
    [bulkTransactionSourceSchema, bulkInvoiceSourceSchema],
);

const bulkSourceRefSchema = z.discriminatedUnion("sourceType", [
    z.object({
        sourceType: z.literal(ScheduleSourceType.transaction),
        transactionId: z.number().int().positive(),
    }),
    z.object({
        sourceType: z.literal(ScheduleSourceType.invoice),
        invoiceId: z.string().uuid(),
    }),
]);

export type ApplyScheduleTemplateBulkSourceRef = z.infer<
    typeof bulkSourceRefSchema
>;

export function getScheduleBulkSourceKey(
    source: ApplyScheduleTemplateBulkSourceRef,
): string {
    return source.sourceType === ScheduleSourceType.transaction
        ? `transaction:${source.transactionId}`
        : `invoice:${source.invoiceId}`;
}

export const applyScheduleTemplateBulkBodySchema = z.object({
    entityId: z.number().int().positive(),
    ledgerId: z.string().uuid().optional(),
    /** One recognition start for the batch; read only by rows anchored on a specific date. */
    specificDate: z.string().date().optional(),
    sources: z
        .array(applyScheduleTemplateBulkSourceSchema)
        .min(1)
        .max(APPLY_SCHEDULE_TEMPLATE_BULK_LIMIT)
        .refine(
            (sources) =>
                new Set(sources.map(getScheduleBulkSourceKey)).size ===
                sources.length,
            { message: "Sources must be unique" },
        ),
});

export const applyScheduleTemplateBulkResponseSchema = z.object({
    applied: z.array(
        z.object({
            source: bulkSourceRefSchema,
            applicationId: z.string().uuid(),
            application: scheduleApplicationBaseSchema,
        }),
    ),
    failed: z.array(
        z.object({
            source: bulkSourceRefSchema,
            message: z.string(),
        }),
    ),
});

export const unapplyScheduleApplicationsBodySchema = z.object({
    entityId: z.number().int().positive(),
    applicationIds: applicationIdsSchema,
});

export const unapplyScheduleApplicationsResponseSchema = z.object({
    unapplied: z.array(z.string().uuid()),
    failed: z.array(
        z.object({
            applicationId: z.string().uuid(),
            message: z.string(),
        }),
    ),
});

export type ApplyScheduleTemplateBody = z.infer<
    typeof applyScheduleTemplateBodySchema
>;

export type ApplyScheduleTemplateResponse = z.infer<
    typeof applyScheduleTemplateResponseSchema
>;

export type ApplyScheduleTemplateBulkSource = z.infer<
    typeof applyScheduleTemplateBulkSourceSchema
>;

export type ApplyScheduleTemplateBulkBody = z.infer<
    typeof applyScheduleTemplateBulkBodySchema
>;

export type ApplyScheduleTemplateBulkResponse = z.infer<
    typeof applyScheduleTemplateBulkResponseSchema
>;

export type UnapplyScheduleApplicationsBody = z.infer<
    typeof unapplyScheduleApplicationsBodySchema
>;

export type UnapplyScheduleApplicationsResponse = z.infer<
    typeof unapplyScheduleApplicationsResponseSchema
>;
